<template>
  <v-card class="pa-4" outlined>
    <div class="d-flex align-center mb-3">
      <div class="text-subtitle-1 font-weight-medium">OEE Por Día y Máquina</div>
      <v-spacer/>
      <v-chip small color="blue lighten-5" text-color="blue darken-2">
        {{ diasLabels.length }} día(s) · {{ maquinasOrden.length }} máquina(s)
      </v-chip>
    </div>

    <div style="overflow-x:auto">
      <v-simple-table dense class="elevation-1">
        <thead>
          <tr>
            <th class="text-left">Máquina</th>
            <th v-for="d in diasLabels" :key="d" class="text-center">{{ d }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="lbl in maquinasOrden" :key="lbl">
            <td class="font-weight-medium">{{ lbl }}</td>
            <td v-for="(day, i) in diasKeys" :key="lbl + '-' + i" class="pa-0">
              <v-sheet :class="cellClass(lbl, day)" class="py-2 px-0 text-center">
                {{ cellLabel(lbl, day) }}
              </v-sheet>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'OeeTablaIgualGrafica',
  props: {
    /** PÁSAME gfData (las filas crudas que usa la gráfica) */
    rows: { type: Array, required: true },
  },

  computed: {
    // Normaliza mínimamente y conserva orden (clave)
    norm () {
      const toNum = v => { const n = Number(v); return Number.isFinite(n) ? n : null }
      return (this.rows || []).map(r => ({
        fecha : this.pickDate(r?.fecha),
        lbl   : String(r?.label ?? r?.nombre ?? r?.maquina ?? r?.id ?? r?.key ?? '—'),
        A     : toNum(r?.A ?? r?.disponibilidad),
        P     : toNum(r?.P ?? r?.rendimiento),
        Q     : toNum(r?.Q ?? r?.calidad),
        oBack : toNum(r?.oee ?? r?.OEE),
      })).filter(x => !!x.fecha && !!x.lbl)
    },

    // Días (ASC) como usa el chart
    diasKeys () {
      return Array.from(new Set(this.norm.map(x => x.fecha))).sort()
    },
    diasLabels () {
      return this.diasKeys.map(k => { const [Y,M,D]=k.split('-'); return `${D}/${M}/${Y}` })
    },

    // Conjunto de etiquetas (máquinas) tal cual aparecen en la gráfica
    maquinasOrden () {
      return Array.from(new Set(this.norm.map(x => x.lbl)))
    },

    /**
     * Índice día -> (label -> fila) tomando **la PRIMERA** aparición
     * (igual que `find()` en tu gráfica).
     */
    idx () {
      const root = new Map()
      for (const r of this.norm) {
        if (!root.has(r.fecha)) root.set(r.fecha, new Map())
        const m = root.get(r.fecha)
        if (!m.has(r.lbl)) m.set(r.lbl, r) // primera gana
      }
      return root
    }
  },

  methods: {
    pickDate (v) {
      if (!v) return null
      if (typeof v === 'string') return v.slice(0,10)
      if (v instanceof Date) return v.toISOString().slice(0,10)
      const m = String(v).match(/\d{4}-\d{2}-\d{2}/); return m ? m[0] : null
    },
    clamp (n, lo, hi) { n = Number(n); return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : null },

    // Igual que el chart: usa oee del back si existe; si no, A*P*Q/10000
    calcOee (row) {
      if (!row) return null
      if (Number.isFinite(row.oBack)) return this.clamp(row.oBack, 0, 100)
      const A = this.clamp(row.A, 0, 100), P = this.clamp(row.P, 0, 100), Q = this.clamp(row.Q, 0, 100)
      if (A==null || P==null || Q==null) return null
      return this.clamp((A * P * Q) / 10000, 0, 100)
    },

    cellLabel (lbl, day) {
      const r = this.idx.get(day)?.get(lbl) || null
      const o = this.calcOee(r)
      if (o == null) return '—'

      const v = Math.floor(Number(o || 0))
      return `${v}%`
    },
    cellClass (lbl, day) {
      const o = this.calcOee(this.idx.get(day)?.get(lbl) || null)
      if (o == null) return 'grey lighten-3'
      if (o >= 90)   return 'green darken-2 white--text'
      if (o >= 80)   return 'light-green darken-1 white--text'
      if (o >= 70)   return 'amber darken-2 black--text'
      if (o >= 60)   return 'orange darken-1 white--text'
      if (o >= 40)   return 'deep-orange darken-1 white--text'
      return 'red darken-2 white--text'
    }
  }
}
</script>

<style scoped>
th, td { white-space: nowrap; }
</style>
